import { Box, Button, CircularProgress, IconButton, LinearProgress, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useEffect } from "react";

import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckIcon from "@mui/icons-material/Check";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import { useDesktopUpdate } from "../lib/notifications/useDesktopUpdate.js";
import {
  formatProgressCaption,
  formatProgressLine,
} from "../lib/notifications/downloadFormat.js";

/**
 * Visible desktop self-update prompt. Renders nothing in a plain browser or
 * when idle. The *actionable* states are shown as a **centered, dimmed-backdrop
 * modal** so the user can't miss a waiting update; the transient feedback
 * states stay as an unobtrusive bottom-right toast:
 *
 *   MODAL (center screen, blocks the UI):
 *     available  → "vX.Y.Z is available"   [Update now] [Later]   + release notes
 *     downloading→ "12.4 MB / 48.0 MB" + a moving bar + "26% · 1.8 MB/s ·
 *                  ~18s left" caption (progress, not dismissable)
 *     ready      → "Update ready"           [Restart now] [Later]
 *     error      → "Update failed: …"       [Retry] [Dismiss]
 *
 *   TOAST (corner, non-blocking):
 *     checking   → "Checking for updates…"  (spinner)
 *     uptodate   → "You're on the latest version" (auto-dismisses)
 *
 * Mounted once at the app root (App.tsx). The tray "Check for Updates…" item
 * drives the checking/uptodate states via the useDesktopUpdate hook.
 */
export function UpdateBanner(): JSX.Element | null {
  const {
    status,
    version,
    notes,
    percent,
    downloaded,
    contentLength,
    bytesPerSecond,
    error,
    install,
    restart,
    dismiss,
    retry,
  } = useDesktopUpdate();

  const isModal =
    status === "available" ||
    status === "downloading" ||
    status === "ready" ||
    status === "error";

  // A modal state is dismissable except mid-download (no clean cancel; it
  // transitions to ready/error on its own). uptodate (toast) is dismissable too.
  const dismissable =
    status === "available" ||
    status === "ready" ||
    status === "error" ||
    status === "uptodate";

  // For the modal: let Escape close it (the primary action auto-focuses via
  // the autoFocus prop on its <Button> below).
  useEffect(() => {
    if (!isModal || !dismissable) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") dismiss();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isModal, dismissable, dismiss]);

  if (status === "idle") return null;

  const iconColor =
    status === "error"
      ? "error.main"
      : status === "uptodate"
        ? "success.main"
        : "primary.main";

  // `checking` shows a spinner; every other state a static glyph.
  const renderIcon = (size: number): JSX.Element => {
    if (status === "checking") {
      return <CircularProgress size={size} sx={{ color: iconColor }} aria-hidden />;
    }
    const IconCmp =
      status === "error"
        ? WarningAmberIcon
        : status === "uptodate"
          ? CheckIcon
          : status === "ready"
            ? RefreshIcon
            : DownloadIcon;
    return <IconCmp sx={{ fontSize: size, color: iconColor }} aria-hidden />;
  };

  const title =
    status === "checking"
      ? "Checking for updates…"
      : status === "available"
        ? "Update available"
        : status === "downloading"
          ? "Downloading update…"
          : status === "ready"
            ? "Update ready"
            : status === "uptodate"
              ? "You're on the latest version"
              : "Update failed";

  const description =
    status === "checking"
      ? "Contacting the update server…"
      : status === "available"
        ? `XOP Command Center ${version ?? ""} is ready to install.`.trim()
        : status === "downloading"
          ? formatProgressLine(downloaded, contentLength)
          : status === "ready"
            ? `Restart to finish updating${version ? ` to ${version}` : ""}.`
            : status === "uptodate"
              ? version
                ? `Version ${version}`
                : "No updates available."
              : error || "Something went wrong with the update.";

  const closeButton = dismissable ? (
    <IconButton
      type="button"
      onClick={dismiss}
      aria-label="Dismiss update notification"
      sx={{
        position: "absolute",
        right: 12,
        top: 12,
        height: 28,
        width: 28,
        color: "text.disabled",
        "&:hover": { bgcolor: "action.hover", color: "text.primary" },
      }}
    >
      <CloseIcon sx={{ fontSize: 16 }} aria-hidden />
    </IconButton>
  ) : null;

  // ---- MODAL: center screen, dimmed backdrop (the "never miss it" path) ----
  if (isModal) {
    return (
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: 1300,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "rgba(0,0,0,0.6)",
          p: 2,
          backdropFilter: "blur(4px)",
        }}
        onMouseDown={
          dismissable
            ? (e) => {
                if (e.target === e.currentTarget) dismiss();
              }
            : undefined
        }
      >
        <Box
          role="dialog"
          aria-modal="true"
          aria-labelledby="update-modal-title"
          data-testid="update-banner"
          sx={{
            position: "relative",
            width: "min(92vw, 440px)",
            borderRadius: 1,
            border: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
            p: 3,
            textAlign: "center",
            color: "text.primary",
            boxShadow: "0 24px 60px -12px rgba(0,0,0,0.55)",
          }}
        >
          {closeButton}

          <Box
            aria-hidden
            sx={{
              mx: "auto",
              mb: 2,
              display: "inline-flex",
              height: 56,
              width: 56,
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              bgcolor: "action.hover",
            }}
          >
            {renderIcon(24)}
          </Box>

          <Typography
            id="update-modal-title"
            component="h2"
            variant="h2"
            sx={{ fontSize: "1.25rem", fontWeight: 600, lineHeight: 1.2, color: "text.primary" }}
          >
            {title}
          </Typography>
          <Typography sx={{ mx: "auto", mt: 0.75, maxWidth: "34ch", fontSize: 14, color: "text.disabled" }}>
            {description}
          </Typography>

          {status === "available" && notes ? (
            <Typography
              sx={{
                mx: "auto",
                mt: 1.5,
                maxHeight: 96,
                overflow: "auto",
                whiteSpace: "pre-line",
                borderRadius: 1,
                bgcolor: "action.hover",
                p: 1.5,
                textAlign: "left",
                fontSize: 12,
                color: "text.secondary",
              }}
            >
              {notes}
            </Typography>
          ) : null}

          {status === "downloading" ? (
            <>
              {/* A determinate bar advances with the real percent once the
                  total size is known; if the server didn't advertise a
                  Content-Length we fall back to an indeterminate (animated)
                  bar so it still visibly moves rather than sitting at 0%. */}
              <LinearProgress
                variant={percent != null ? "determinate" : "indeterminate"}
                value={percent ?? 0}
                aria-label="Update download progress"
                sx={{ mt: 2, height: 8, borderRadius: 999 }}
              />
              {(() => {
                const caption = formatProgressCaption(
                  percent,
                  downloaded,
                  contentLength,
                  bytesPerSecond,
                );
                return caption ? (
                  <Typography
                    data-testid="update-progress-caption"
                    sx={{ mt: 1, fontSize: 12, color: "text.secondary", fontVariantNumeric: "tabular-nums" }}
                  >
                    {caption}
                  </Typography>
                ) : null;
              })()}
            </>
          ) : null}

          <Box sx={{ mt: 2.5, display: "flex", flexDirection: "column", gap: 1 }}>
            {status === "available" && (
              <>
                <Button
                  autoFocus
                  variant="contained"
                  fullWidth
                  onClick={install}
                  data-testid="update-install"
                  startIcon={<DownloadIcon sx={{ fontSize: 16 }} aria-hidden />}
                >
                  Update now
                </Button>
                <Button variant="text" color="inherit" fullWidth onClick={dismiss}>
                  Later
                </Button>
              </>
            )}
            {status === "ready" && (
              <>
                <Button
                  autoFocus
                  variant="contained"
                  fullWidth
                  onClick={restart}
                  data-testid="update-restart"
                  startIcon={<RefreshIcon sx={{ fontSize: 16 }} aria-hidden />}
                >
                  Restart now
                </Button>
                <Button variant="text" color="inherit" fullWidth onClick={dismiss}>
                  Later
                </Button>
              </>
            )}
            {status === "error" && (
              <>
                <Button autoFocus variant="contained" fullWidth onClick={retry} data-testid="update-retry">
                  Retry
                </Button>
                <Button variant="text" color="inherit" fullWidth onClick={dismiss}>
                  Dismiss
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Box>
    );
  }

  // ---- TOAST: corner, non-blocking (checking | uptodate) ----
  return (
    <Box
      role="status"
      aria-live="polite"
      data-testid="update-banner"
      sx={{
        position: "fixed",
        bottom: 16,
        right: 16,
        zIndex: 1100,
        width: "min(92vw, 360px)",
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        p: 2,
        color: "text.primary",
        boxShadow: (t) => t.shadows[1],
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
        <Box
          aria-hidden
          sx={{
            mt: 0.25,
            display: "inline-flex",
            height: 32,
            width: 32,
            flexShrink: 0,
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "50%",
            bgcolor: "action.hover",
          }}
        >
          {renderIcon(16)}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography component="p" variant="h6" sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.2, color: "text.primary" }}>
            {title}
          </Typography>
          <Typography component="p" sx={{ mt: 0.25, fontSize: 12, color: "text.disabled" }}>
            {description}
          </Typography>
        </Box>
        {dismissable ? (
          <IconButton
            type="button"
            onClick={dismiss}
            aria-label="Dismiss update notification"
            sx={{
              mr: -0.5,
              mt: -0.5,
              height: 24,
              width: 24,
              color: "text.disabled",
              "&:hover": { color: "text.primary" },
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} aria-hidden />
          </IconButton>
        ) : null}
      </Box>
    </Box>
  );
}
